import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { outerCallApi } from '../services/api'
import { getSocket, reconnectSocket } from '../services/socket'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useLivekitStore } from '../stores/livekitStore'
import { useAuthStore } from '../stores/authStore'
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Loader2 } from 'lucide-react'
import { displayInitial } from '../utils/displayInitial'

function generateGuestName(): string {
  return `Guest${Math.floor(1000 + Math.random() * 9000)}`
}

type CallState = 'loading' | 'form' | 'incoming' | 'connecting' | 'connected' | 'ended'

interface OuterCallMessage {
  id: number
  text: string
  senderName: string
}

export default function OuterCallPage() {
  const { code } = useParams<{ code: string }>()
  const [state, setState] = useState<CallState>('loading')
  const [displayName, setDisplayName] = useState('')
  const [inviteInfo, setInviteInfo] = useState<any>(null)
  const [error, setError] = useState('')
  const [elapsed, setElapsed] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const [messages, setMessages] = useState<OuterCallMessage[]>([])
  const [chatOpen, setChatOpen] = useState(false)
  const [inputText, setInputText] = useState('')
  const msgIdRef = useRef(0)

  const currentUser = useAuthStore((s) => s.user)
  const isAuthenticated = !!currentUser

  const connect = useDmCallStore((s) => s.connect)
  const disconnect = useDmCallStore((s) => s.disconnect)
  const toggleMute = useDmCallStore((s) => s.toggleMute)
  const toggleVideo = useDmCallStore((s) => s.toggleVideo)
  const isAudioTrackReady = useDmCallStore((s) => s.isAudioTrackReady)
  const isVideoTrackReady = useDmCallStore((s) => s.isVideoTrackReady)
  const micPending = useDmCallStore((s) => s.micToggleInFlight)
  const videoPending = useDmCallStore((s) => s.videoToggleInFlight)
  const remoteParticipants = useDmCallStore((s) => s.remoteParticipants)

  useEffect(() => {
    if (!code) return
    outerCallApi.get(code)
      .then((res) => {
        setInviteInfo(res.data)
        if (isAuthenticated) {
          // OSPROD-39: skip the manual Accept button entirely for
          // authenticated users — the user already navigated to /call/<code>
          // intentionally (they clicked the link), forcing them to click
          // Accept again was the source of "empty screen" reports. Go
          // straight to connecting.
          setDisplayName(currentUser?.username || 'User')
          setState('connecting')
        } else {
          setDisplayName(generateGuestName())
          setState('form')
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to load invite')
        setState('ended')
      })
  }, [code, isAuthenticated, currentUser?.username])

  // OSPROD-39: when state flips to 'connecting' (auto-accept for
  // authenticated users, or right after the guest joins), run the
  // actual LiveKit + signalling sequence. Extracted out of click
  // handlers so both code paths share it.
  useEffect(() => {
    if (state !== 'connecting') return
    if (!inviteInfo?.roomName) return
    let cancelled = false
    ;(async () => {
      try {
        // Drop any active channel call — only one LiveKit room can hold
        // the mic at a time. Without this scenario B from the ticket
        // ("active channel call still running, only invitee audible")
        // hits us: the channel-call mic publish stays alive and the
        // outer-call publish never gets the device.
        const lk = useLivekitStore.getState()
        if (lk.room) {
          try { await lk.disconnect() } catch (err) { console.warn('[OuterCall guest] drop active channel call:', err) }
        }
        if (cancelled) return
        const username = isAuthenticated ? (currentUser?.username || 'User') : (displayName || 'Guest')
        await connect(inviteInfo.roomName, username)
        if (cancelled) return
        const socket = getSocket()
        socket.emit('outer:call:register', { roomName: inviteInfo.roomName, role: 'guest' })
        socket.emit('outer:call:accept', { roomName: inviteInfo.roomName })
        if (inviteInfo.callType === 'video') {
          try { await useDmCallStore.getState().toggleVideo() } catch (err) { console.warn('[OuterCall] enable camera:', err) }
        }
        if (!cancelled) setState('connected')
      } catch (err: any) {
        console.error('[OuterCall] connect failed:', err)
        if (!cancelled) {
          setError(err?.message || 'Failed to connect to call')
          setState('ended')
        }
      }
    })()
    return () => { cancelled = true }
    // displayName intentionally excluded — captured at start; we don't
    // want a name re-edit to teardown the in-flight connect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, inviteInfo?.roomName, inviteInfo?.callType, isAuthenticated])

  useEffect(() => {
    const socket = getSocket()
    const handleEnded = (data: any) => {
      if (data.roomName === inviteInfo?.roomName) {
        setState('ended')
        disconnect()
        if (timerRef.current) clearInterval(timerRef.current)
      }
    }
    const handleMessage = (data: any) => {
      if (data.roomName === inviteInfo?.roomName) {
        setMessages((prev) => [...prev, { text: data.text, senderName: data.senderName, id: ++msgIdRef.current }])
      }
    }
    socket.on('outer:call:ended', handleEnded)
    socket.on('outer:call:message', handleMessage)
    return () => {
      socket.off('outer:call:ended', handleEnded)
      socket.off('outer:call:message', handleMessage)
    }
  }, [inviteInfo?.roomName, disconnect])

  useEffect(() => {
    if (state === 'connected') {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [state])

  const handleJoin = async () => {
    if (!code || !displayName.trim()) return
    try {
      const res = await outerCallApi.join(code, displayName.trim())
      localStorage.setItem('guestToken', res.data.guestToken)
      // Reconnect socket with new guest token so outer:call events work
      reconnectSocket()
      // OSPROD-39: skip the manual Accept step for guests too — they
      // already entered their name and clicked Join. The connecting
      // effect picks it up and runs the LiveKit + signalling sequence.
      setState('connecting')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to join')
    }
  }

  const handleEndCall = () => {
    const socket = getSocket()
    socket.emit('outer:call:end', { roomName: inviteInfo?.roomName })
    disconnect()
    setState('ended')
  }

  const handleSendMessage = () => {
    if (!inputText.trim() || !inviteInfo?.roomName) return
    const socket = getSocket()
    const senderName = isAuthenticated ? (currentUser?.username || 'Me') : (displayName || 'Guest')
    socket.emit('outer:call:message', { roomName: inviteInfo.roomName, text: inputText.trim(), senderName })
    setMessages((prev) => [...prev, { text: inputText.trim(), senderName: 'Me', id: ++msgIdRef.current }])
    setInputText('')
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (state === 'loading') {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (state === 'form') {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-xl p-6 w-full max-w-sm shadow-xl">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/20 text-primary flex items-center justify-center mx-auto mb-3">
              <Phone size={28} />
            </div>
            <h2 className="text-foreground text-xl font-medium mb-1">Incoming Call</h2>
            <p className="text-muted-foreground text-sm">
              {inviteInfo?.callerName} is calling you ({inviteInfo?.callType})
            </p>
          </div>

          <div className="space-y-3">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              className="w-full bg-muted rounded-lg px-4 py-2.5 text-foreground placeholder:text-muted-foreground outline-none"
              onKeyDown={(e) => { if (e.key === 'Enter') handleJoin() }}
            />
            <button
              onClick={handleJoin}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition"
            >
              Join Call
            </button>
          </div>

          {error && <div className="text-red-400 text-sm mt-3 text-center">{error}</div>}
        </div>
      </div>
    )
  }

  // OSPROD-39: 'connecting' replaces the old 'incoming' middle screen.
  // Auto-accept means we never show Accept/Decline buttons — instead a
  // visible spinner with a clear message so the user isn't staring at
  // a blank background while the LiveKit handshake happens.
  if (state === 'connecting') {
    return (
      <div className="h-screen w-screen bg-background flex flex-col items-center justify-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-3xl font-bold text-foreground mb-4">
          {displayInitial(inviteInfo?.callerName)}
        </div>
        <div className="text-foreground text-xl font-medium mb-1">{inviteInfo?.callerName}</div>
        <div className="text-muted-foreground text-sm mb-8">Joining {inviteInfo?.callType || ''} call…</div>
        <Loader2 size={32} className="text-primary animate-spin" />
      </div>
    )
  }

  if (state === 'ended') {
    return (
      <div className="h-screen w-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-foreground text-lg mb-2">Call ended</div>
          <div className="text-muted-foreground text-sm">{error || 'The call has ended.'}</div>
        </div>
      </div>
    )
  }

  // Connected state
  return (
    <div className="h-screen w-screen bg-background flex flex-col">
      {/* Top bar */}
      <div className="h-14 bg-card flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
            {displayInitial(inviteInfo?.callerName)}
          </div>
          <div>
            <div className="text-foreground text-sm font-medium">{inviteInfo?.callerName}</div>
            <div className="text-muted-foreground text-xs">{formatTime(elapsed)}</div>
          </div>
        </div>
        <button
          onClick={() => setChatOpen(!chatOpen)}
          className="text-muted-foreground hover:text-foreground text-sm"
        >
          {chatOpen ? 'Hide Chat' : `Chat (${messages.length})`}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Call area */}
        <div className="flex-1 flex flex-col relative">
          {/* Remote avatar / video placeholder */}
          <div className="flex-1 flex items-center justify-center">
            {remoteParticipants.length > 0 ? (
              <div className="text-center">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-5xl font-bold text-foreground mx-auto mb-4">
                  {displayInitial(inviteInfo?.callerName)}
                </div>
                <div className="text-foreground font-medium">{inviteInfo?.callerName}</div>
              </div>
            ) : (
              <div className="text-muted-foreground">Connecting...</div>
            )}
          </div>

          {/* Controls */}
          <div className="h-20 bg-card flex items-center justify-center gap-6 shrink-0">
            <button
              onClick={micPending ? undefined : toggleMute}
              disabled={micPending}
              aria-busy={micPending || undefined}
              title={micPending ? 'Updating microphone…' : isAudioTrackReady ? 'Mute' : 'Unmute'}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
                isAudioTrackReady ? 'bg-accent text-foreground' : 'bg-red-500/20 text-red-400'
              } ${micPending ? 'opacity-60 cursor-wait' : ''}`}
            >
              {micPending ? <Loader2 size={18} className="animate-spin" /> : isAudioTrackReady ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            <button
              onClick={videoPending ? undefined : toggleVideo}
              disabled={videoPending}
              aria-busy={videoPending || undefined}
              title={videoPending ? 'Updating camera…' : isVideoTrackReady ? 'Turn off camera' : 'Turn on camera'}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition ${
                isVideoTrackReady ? 'bg-accent text-foreground' : 'bg-red-500/20 text-red-400'
              } ${videoPending ? 'opacity-60 cursor-wait' : ''}`}
            >
              {videoPending ? <Loader2 size={18} className="animate-spin" /> : isVideoTrackReady ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
            <button
              onClick={handleEndCall}
              className="w-14 h-14 rounded-full bg-red-500 text-foreground flex items-center justify-center hover:bg-red-600 transition"
            >
              <PhoneOff size={24} />
            </button>
          </div>
        </div>

        {/* Chat panel */}
        {chatOpen && (
          <div className="w-80 bg-card border-l border-border flex flex-col">
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
              {messages.map((msg) => (
                <div key={msg.id} className="text-sm">
                  <span className="text-primary font-medium">{msg.senderName}:</span>{' '}
                  <span className="text-foreground">{msg.text}</span>
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-muted-foreground text-xs text-center py-4">No messages yet</div>
              )}
            </div>
            <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
              <input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage() }}
                placeholder="Type a message..."
                className="flex-1 bg-transparent text-foreground text-sm outline-none"
              />
              <button
                onClick={handleSendMessage}
                className="text-primary hover:text-primary/90 text-sm font-medium"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
